import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { APPLICANTS_API } from '../../utils/consts'
import ApplicantsComponent from './pages/Applicants'
import formatDataForTable from './utils/formatDataForTable'
import getQuery from './utils/getQuery'
import Swal from 'sweetalert2'
import { saveAs } from 'file-saver'
import { getBlobErrorMessage } from '../../utils/getBlobErrorMessage'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      keepPreviousData: true,
      staleTime: 120 * 1000
    }
  }
})

export const useGetApplicants = (query) => {
  return useQuery({
    queryKey: ['applicants', query],
    queryFn: async () => {
      const queryText = getQuery(query)
      const { data } = await axios.get(`${APPLICANTS_API}/applicants?${queryText}`, { withCredentials: false })
      if (data) {
        const _records = data.records
        const _applicants = data.result.map((item) => formatDataForTable(item))
        const _statistics = data.statistics
        const _currentStatistics = data.currentStatistics
        return { applicants: _applicants, records: _records, statistics: _statistics, currentStatistics: _currentStatistics }
      }
    },
    refetchOnMount: 'always'
  })
}

export const useApplicantHistory = (query) => {
  return useQuery({
    queryKey: ['applicantHistory', query.status, query.jobPosition],
    queryFn: async () => {
      const response = await axios.get(`${APPLICANTS_API}/applicants/applicantHistory`, {
        params: {
          status: query.status,
          ...(query.jobPosition && { jobPosition: query.jobPosition })
        },
        withCredentials: false
      })
      return response.data
    }
  })
}

export const useGetJobPositions = () => {
  return useQuery({
    queryKey: ['jobPositions'],
    queryFn: async () => {
      const { data } = await axios.get(`${APPLICANTS_API}/applicants/jobPosition`, { withCredentials: false })
      return data.jobPositions || []
    }
  })
}

export const useGetApplicantHistory = (applicantId) => {
  return useQuery({
    queryKey: ['applicantHistoryById', applicantId],
    queryFn: async () => {
      const response = await axios.get(`${APPLICANTS_API}/applicants/getOneHistory?id=${applicantId}`, { withCredentials: false })
      return response.data
    },
    enabled: !!applicantId
  })
}

export const downloadCsv = async (query, searchTxt) => {
  try {
    Swal.fire({
      title: 'Ju lutem prisni...',
      text: 'Duke gjeneruar dokumentin',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    const queryText = getQuery(query, searchTxt)
    const response = await axios.get(`${APPLICANTS_API}/applicants/csv/applicants?${queryText}`, {
      responseType: 'blob',
      withCredentials: false
    })
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
    saveAs(blob, 'Aplikantet.csv')
    Swal.close()
    Swal.fire({
      title: 'Sukses!',
      text: 'Dokumenti u shkarkua me sukses.',
      icon: 'success'
    })
  } catch (error) {
    console.log('CSV download error', error)

    Swal.close()
    Swal.fire({
      title: 'Gabim',
      text: 'Ndodhi një gabim gjatë shkarkimit të dokumentit.',
      icon: 'error'
    })
  }
}

export const downloadZipDocs = async (query) => {
  const { dateFrom, dateTo, jobPosition } = query || {}

  if (!jobPosition && (!dateFrom || !dateTo)) {
    Swal.fire({
      title: 'Gabim',
      text: 'Ju lutem zgjidhni një pozitë pune ose datën "From" dhe "To" për të shkarkuar CV-të.',
      icon: 'warning'
    })
    return
  }

  if (Boolean(dateFrom) !== Boolean(dateTo)) {
    Swal.fire({
      title: 'Gabim',
      text: 'Ju lutem zgjidhni të dy datat, "From" dhe "To".',
      icon: 'warning'
    })
    return
  }

  try {
    Swal.fire({
      title: 'Ju lutem prisni...',
      text: 'Duke gjeneruar dokumentin',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    const response = await axios.get(`${APPLICANTS_API}/applicants/download-zip-docs`, {
      responseType: 'blob',
      withCredentials: false,
      params: {
        ...(dateFrom && dateTo && { from: dateFrom, to: dateTo }),
        ...(jobPosition && { jobPosition })
      }
    })

    const contentType = response.headers['content-type'] || ''

    if (contentType.includes('application/json')) {
      const errorMessage = await getBlobErrorMessage(response.data)
      Swal.close()
      Swal.fire({
        title: 'Gabim',
        text: errorMessage || 'Ndodhi një gabim gjatë shkarkimit të dokumentit.',
        icon: 'error'
      })
      return
    }

    const blob = new Blob([response.data], { type: 'application/zip' })
    saveAs(blob, 'applicant-docs.zip')

    Swal.close()
    Swal.fire({
      title: 'Sukses!',
      text: 'Dokumenti u shkarkua me sukses.',
      icon: 'success'
    })
  } catch (error) {
    const errorMessage = await getBlobErrorMessage(error.response?.data)    
    Swal.close()
    Swal.fire({
      title: 'Gabim',
      text: errorMessage || 'Ndodhi një gabim gjatë shkarkimit të dokumentit.',
      icon: 'error'
    })
  }
}

export const dismissBlacklistAlert = async (applicantId) => {
  const response = await axios.patch(
    `${APPLICANTS_API}/applicants/dismissBlacklistAlert/${applicantId}`,
    {},
    { withCredentials: false }
  )
  return response.data
}

export const dismissReturningEmployeeAlert = async (applicantId) => {
  const response = await axios.patch(
    `${APPLICANTS_API}/applicants/dismissReturningEmployeeAlert/${applicantId}`,
    {},
    { withCredentials: false }
  )
  return response.data
}

const Applicants = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ApplicantsComponent />
    </QueryClientProvider>
  )
}

export default Applicants
